import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import clinicService from '../../../../../services/clinic.service';
import Loading from '../../../../common/Loading';
import { formatPmsType } from '../../../../../constants/pmsTypes';

const emptyForm = (clinicId) => ({
    clinicId,
    enabled: false,
    filePrefix: '',
    dropDirectory: '',
    users: [],
});

function OptosysTab() {
    const { clinicId } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [clinicUsers, setClinicUsers] = useState([]);
    const [form, setForm] = useState(emptyForm(clinicId));
    const [configured, setConfigured] = useState(false);
    const [pmsType, setPmsType] = useState('NONE');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [pmsRes, optosysRes, usersRes] = await Promise.all([
                clinicService.getPms(clinicId),
                clinicService.getOptosys(clinicId),
                clinicService.getUsers(clinicId, 'ALL'),
            ]);
            setPmsType(pmsRes.data?.pmsType || 'NONE');
            const data = optosysRes.data || {};
            const hasConfig = Boolean(data.filePrefix);
            setConfigured(hasConfig);
            setForm({
                clinicId,
                enabled: Boolean(data.enabled),
                filePrefix: data.filePrefix || '',
                dropDirectory: data.dropDirectory || '',
                users: (data.users || []).map((u) => ({
                    optosysDocId: u.optosysDocId || '',
                    userId: u.userId || '',
                    defaultUser: Boolean(u.defaultUser),
                })),
            });
            setClinicUsers(usersRes.data || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load Optosys configuration');
            setForm(emptyForm(clinicId));
            setConfigured(false);
            setPmsType('NONE');
        } finally {
            setLoading(false);
        }
    }, [clinicId]);

    useEffect(() => {
        load();
    }, [load]);

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateUserRow = (index, field, value) => {
        setForm((prev) => {
            const users = prev.users.map((row, i) => {
                if (i !== index) {
                    return field === 'defaultUser' && value ? { ...row, defaultUser: false } : row;
                }
                return { ...row, [field]: value };
            });
            return { ...prev, users };
        });
    };

    const addUserRow = () => {
        setForm((prev) => ({
            ...prev,
            users: [
                ...prev.users,
                {
                    optosysDocId: '',
                    userId: clinicUsers[0]?.id || '',
                    defaultUser: prev.users.length === 0,
                },
            ],
        }));
    };

    const removeUserRow = (index) => {
        setForm((prev) => ({
            ...prev,
            users: prev.users.filter((_, i) => i !== index),
        }));
    };

    const handleSave = async () => {
        if (!form.filePrefix.trim()) {
            toast.error('File prefix is required');
            return;
        }
        if (form.enabled && form.users.length === 0) {
            toast.error('Add at least one doctor mapping when import is enabled');
            return;
        }
        if (form.enabled && !form.users.some((u) => u.defaultUser)) {
            toast.error('Mark one mapping as the default user');
            return;
        }
        for (const row of form.users) {
            if (!row.userId) {
                toast.error('Each mapping needs an EyeConX user');
                return;
            }
        }

        setSaving(true);
        try {
            const payload = {
                clinicId,
                enabled: form.enabled,
                filePrefix: form.filePrefix.trim(),
                dropDirectory: form.dropDirectory.trim() || null,
                users: form.users.map((u) => ({
                    optosysDocId: u.optosysDocId.trim() || null,
                    userId: u.userId,
                    defaultUser: Boolean(u.defaultUser),
                })),
            };
            const res = await clinicService.saveOptosys(clinicId, payload);
            const data = res.data || {};
            setConfigured(Boolean(data.filePrefix));
            setForm({
                clinicId,
                enabled: Boolean(data.enabled),
                filePrefix: data.filePrefix || '',
                dropDirectory: data.dropDirectory || '',
                users: (data.users || []).map((u) => ({
                    optosysDocId: u.optosysDocId || '',
                    userId: u.userId || '',
                    defaultUser: Boolean(u.defaultUser),
                })),
            });
            toast.success('Optosys configuration saved');
        } catch (err) {
            console.error(err);
            const message = err?.response?.data?.message || 'Failed to save Optosys configuration';
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!configured) {
            return;
        }
        if (!window.confirm('Remove Optosys enrollment for this clinic? Import will stop on the next cron run.')) {
            return;
        }
        setSaving(true);
        try {
            await clinicService.deleteOptosys(clinicId);
            setForm(emptyForm(clinicId));
            setConfigured(false);
            toast.success('Optosys configuration removed');
        } catch (err) {
            console.error(err);
            toast.error('Failed to remove Optosys configuration');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (pmsType !== 'OPTOSYS') {
        return (
            <div className="notifications-panel">
                <div className="notifications-panel__header">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-hdd-network"></i>
                        Optosys PMS Import
                    </span>
                </div>
                <div className="notifications-panel__body">
                    <div className="alert alert-warning mb-0" role="alert">
                        <strong>PMS is set to {formatPmsType(pmsType)}.</strong>
                        {' '}
                        Set PMS to <strong>Optosys</strong> in the Info tab (PMS card below Assets Directory) before configuring Optosys import.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-hdd-network"></i>
                    Optosys PMS Import
                </span>
                <div className="d-flex gap-2">
                    {configured && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleDelete}
                            disabled={saving}
                        >
                            <i className="bi bi-trash me-1"></i>
                            Remove
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        <i className="bi bi-check2 me-1"></i>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>

            <div className="notifications-panel__body">
                <p className="text-muted small mb-3">
                    Enable Optosys for clinics that drop CSV files into this server&apos;s inbox.
                    The EyeconX-Optosys worker imports enabled clinics on its cron schedule.
                </p>

                <div className="form-check form-switch mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="optosys-enabled"
                        checked={form.enabled}
                        onChange={(e) => updateField('enabled', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="optosys-enabled">
                        Enable Optosys import for this clinic
                    </label>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="optosys-file-prefix">
                            File prefix <span className="text-danger">*</span>
                        </label>
                        <input
                            id="optosys-file-prefix"
                            className="form-control"
                            placeholder="c2990"
                            value={form.filePrefix}
                            onChange={(e) => updateField('filePrefix', e.target.value)}
                        />
                        <div className="form-text">CSV names: {'{prefix}_patient.csv'}, etc.</div>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="optosys-drop-directory">
                            Drop directory
                        </label>
                        <input
                            id="optosys-drop-directory"
                            className="form-control"
                            placeholder="c2990 (optional; defaults to file prefix)"
                            value={form.dropDirectory}
                            onChange={(e) => updateField('dropDirectory', e.target.value)}
                        />
                        <div className="form-text">Relative to inbox-root, or an absolute path.</div>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Doctor mappings</h6>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addUserRow}>
                        <i className="bi bi-plus-lg me-1"></i>
                        Add mapping
                    </button>
                </div>

                {form.users.length === 0 ? (
                    <div className="text-muted small border rounded p-3">
                        No mappings yet. Add at least one (with a default) before enabling import.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Optosys doc id</th>
                                    <th>EyeConX user</th>
                                    <th style={{ width: 100 }}>Default</th>
                                    <th style={{ width: 60 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {form.users.map((row, index) => (
                                    <tr key={`optosys-user-${index}`}>
                                        <td>
                                            <input
                                                className="form-control form-control-sm"
                                                placeholder="Optional UUID from Optosys"
                                                value={row.optosysDocId}
                                                onChange={(e) => updateUserRow(index, 'optosysDocId', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                value={row.userId}
                                                onChange={(e) => updateUserRow(index, 'userId', e.target.value)}
                                            >
                                                <option value="">Select user</option>
                                                {clinicUsers.map((user) => (
                                                    <option key={user.id} value={user.id}>
                                                        {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.username}
                                                        {user.role ? ` (${user.role})` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="text-center">
                                            <input
                                                type="radio"
                                                name="optosys-default-user"
                                                checked={Boolean(row.defaultUser)}
                                                onChange={() => updateUserRow(index, 'defaultUser', true)}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => removeUserRow(index)}
                                                aria-label="Remove mapping"
                                            >
                                                <i className="bi bi-x-lg"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default OptosysTab;
